/**
 * fetch() with a hard client-side deadline, shared by the dashboard and the
 * browser SDK. Built on AbortController + setTimeout because our browserslist
 * floor predates AbortSignal.timeout()/AbortSignal.any(). A deadline abort is
 * rethrown as FetchTimeoutError so callers can report it to Sentry distinctly;
 * an abort coming from the caller's own signal (e.g. react-query cancelling a
 * stale query) passes through unchanged and must not be reported as an error.
 *
 * fetch() resolves once response headers arrive, but json()/text() stream the
 * body and can stall the same way — so those reads on the returned Response
 * are wrapped with their own deadline on the same controller (worst-case wall
 * time is therefore 2×timeoutMs: headers, then body).
 */
/**
 * Deadline for dashboard and SDK API fetches. Deliberately above the server's
 * 60s /api watchdog (server/utils/requestTimeout.ts): any server-side outcome
 * — a specific 500 from the 30s pg query_timeout, a slow success, or the
 * watchdog's 504 — reaches the caller before the client aborts. A client-side
 * abort is not free: the server closes its per-request pg client on 'close',
 * which can leave a partially written ingest batch — so this fires only when
 * the server is unreachable or the response got lost.
 */
export const API_FETCH_TIMEOUT_MS = 65_000;

export class FetchTimeoutError extends Error {
  constructor(url: string, timeoutMs: number) {
    super(`Request timed out after ${timeoutMs}ms: ${url}`);
    this.name = "FetchTimeoutError";
  }
}

export async function fetchWithTimeout(
  url: string,
  init: RequestInit,
  timeoutMs: number,
  signal?: AbortSignal,
): Promise<Response> {
  const controller = new AbortController();
  let timedOut = false;
  const abortOnDeadline = () => {
    timedOut = true;
    controller.abort();
  };
  const abortFromCaller = () => controller.abort();
  if (signal?.aborted) {
    controller.abort();
  }
  signal?.addEventListener("abort", abortFromCaller);

  const rethrow = (error: unknown): never => {
    throw timedOut ? new FetchTimeoutError(url, timeoutMs) : error;
  };

  const headerTimer = setTimeout(abortOnDeadline, timeoutMs);
  let response: Response;
  try {
    response = await fetch(url, { ...init, signal: controller.signal });
  } catch (error) {
    signal?.removeEventListener("abort", abortFromCaller);
    return rethrow(error);
  } finally {
    clearTimeout(headerTimer);
  }

  // The caller-signal listener intentionally stays attached so an external
  // abort still cancels the body read; it is released with the controller.
  const guardBodyRead = <T>(read: () => Promise<T>) => (): Promise<T> => {
    const bodyTimer = setTimeout(abortOnDeadline, timeoutMs);
    return read()
      .catch((error: unknown) => rethrow(error))
      .finally(() => clearTimeout(bodyTimer));
  };
  if (typeof response.json === "function") {
    response.json = guardBodyRead(response.json.bind(response));
  }
  if (typeof response.text === "function") {
    response.text = guardBodyRead(response.text.bind(response));
  }
  return response;
}
